import React, { useEffect, useState } from "react";
import { Images } from "lucide-react";
import { api } from "@/lib/api";
import PexelsKeyForm from "./PexelsKeyForm";
import ReimagePanel from "./ReimagePanel";
import MakeCompliantPanel from "./MakeCompliantPanel";

export const POLICY_LABELS = {
  network: "Network default",
  stock: "Stock photo (Pexels) → branded card fallback",
  card: "Branded headline card (site palette, $0)",
  source: "Publisher's image (re-host source photo — copyright risk)",
};

const NETWORK = []; // stable "whole network" selector

function Row({ label, children }) {
  return (
    <div className="px-5 py-3 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-3 items-start">
      <div className="text-sm text-zinc-300 pt-1">{label}</div>
      <div>{children}</div>
    </div>
  );
}

/** Settings → "Article images": network image policy, Pexels key, stats, network-wide re-image. */
export default function ImagePolicySettings() {
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState("");

  async function load() {
    try { const r = await api.get("/images/config"); setCfg(r.data); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
  }
  useEffect(() => { load(); }, []);

  async function setDefault(p) {
    setErr("");
    try { const r = await api.put("/images/config", { default_policy: p }); setCfg((c) => ({ ...c, ...r.data })); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
  }

  const counts = cfg?.counts || {};
  const q = cfg?.stats?.quota || {};

  return (
    <div data-testid="image-policy-settings" className="border border-zinc-800 rounded-sm">
      <div className="px-5 py-3 border-b border-zinc-800 label-tiny flex items-center gap-2"><Images size={12} className="text-amber-500" /> Article images</div>
      <div className="divide-y divide-zinc-800">
        <Row label="Network default policy">
          <select
            data-testid="image-default-policy" value={cfg?.default_policy || "stock"} onChange={(e) => setDefault(e.target.value)} disabled={!cfg}
            className="admin-input bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm max-w-md w-full"
          >
            {(cfg?.policies || ["stock", "card", "source"]).map((p) => <option key={p} value={p}>{POLICY_LABELS[p]}</option>)}
          </select>
          <div className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
            Applies to every site left on "Network default". Stock and card images are original or licensed — the publisher's photo is never copied.
            {!cfg?.r2_configured && <span className="text-red-400"> R2 is not configured — generated images cannot be hosted.</span>}
          </div>
        </Row>
        <Row label="Pexels (free stock photos)">
          <PexelsKeyForm cfg={cfg} onChange={(c) => setCfg((prev) => ({ ...prev, ...c }))} />
        </Row>
        <Row label="Status">
          <div data-testid="image-counts" className="text-xs mono flex gap-4 flex-wrap">
            <span className="text-emerald-400">stock {counts.stock ?? "…"}</span>
            <span className="text-sky-400">card {counts.card ?? "…"}</span>
            <span className={counts.legacy ? "text-amber-400" : "text-zinc-500"}>source/legacy {counts.legacy ?? "…"}</span>
            <span className="text-zinc-500">published {counts.published ?? "…"}</span>
            <span className="text-zinc-500">· Pexels {q.hour_used ?? 0}/{q.hourly_max ?? 200} this hour · {q.month_used ?? 0}/{q.monthly_max ?? 20000} this month</span>
          </div>
        </Row>
        <Row label="Make all sites compliant">
          <MakeCompliantPanel cfg={cfg} onDone={load} />
        </Row>
        <Row label="Existing articles">
          <ReimagePanel siteIds={NETWORK} onDone={load} />
        </Row>
        {err && <div className="px-5 py-3 text-xs text-red-400 mono">{err}</div>}
      </div>
    </div>
  );
}
